import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { PaperPlaneRight, ChatCircleText } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function MessageThread({ listUrl, postUrl, self }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(listUrl); setMsgs(data); } catch { /* ignore */ }
  }, [listUrl]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await api.post(postUrl, { body: text.trim() }); setText(""); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to send"); }
    finally { setSending(false); }
  };
  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/50 flex flex-col" data-testid="message-thread">
      <div className="max-h-96 overflow-auto p-4 space-y-3">
        {msgs.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-10 flex flex-col items-center gap-2">
            <ChatCircleText size={32} className="text-zinc-600" /> No messages yet. Say hello.
          </div>
        ) : msgs.map((m) => {
          const mine = m.sender === self;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-white/10 text-foreground rounded-bl-sm"}`}>
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                  {m.sender === "admin" ? "DAFARGOS" : "You"} · {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="border-t border-white/10 p-3 flex items-center gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} placeholder="Type a message…" data-testid="message-input" className="bg-zinc-900/60 border-white/10" />
        <Button onClick={send} disabled={sending || !text.trim()} data-testid="send-message-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197] shrink-0"><PaperPlaneRight size={18} weight="fill" /></Button>
      </div>
    </div>
  );
}
